/**
 * Money — ARCHITECTURE.md §0.1.
 *
 * Every amount in this system is a non-negative integer count of *santim*,
 * the minor unit of the Ethiopian birr (1 ETB = 100 santim). Never a float.
 *
 * We carry it as a JS `bigint`, not `number`. Node's `number` is exact only up
 * to 2^53-1; the DB column is `BIGINT` (2^63-1). Using `bigint` end-to-end means
 * the type that leaves Postgres is the same type the domain arithmetic runs on,
 * so there is no silent precision cliff at the boundary.
 *
 * Direction (credit vs debit) is expressed by the ledger posting, never by the
 * sign of an amount. A negative `Santim` is a bug, and the constructors below
 * are where that bug gets caught.
 */
export const SANTIM_PER_ETB = 100n;
/** Largest value the DB's BIGINT column can hold. */
export const MAX_SANTIM = 9223372036854775807n;
/** Smallest value the DB's BIGINT column can hold. */
export const MIN_SANTIM = -9223372036854775808n;
export class MoneyError extends Error {
    name = 'MoneyError';
}
/**
 * The single entry point for creating an amount. Accepts a bigint, an integral
 * number, or a decimal-free string (what `pg` hands back for a BIGINT column).
 */
export function santim(value) {
    let v;
    if (typeof value === 'bigint') {
        v = value;
    }
    else if (typeof value === 'number') {
        if (!Number.isInteger(value)) {
            throw new MoneyError(`amount must be an integer number of santim, got ${value}`);
        }
        if (!Number.isSafeInteger(value)) {
            throw new MoneyError(`amount ${value} exceeds the safe integer range; pass a bigint or string`);
        }
        v = BigInt(value);
    }
    else {
        if (!/^-?\d+$/.test(value)) {
            throw new MoneyError(`amount string must be a plain integer, got ${JSON.stringify(value)}`);
        }
        v = BigInt(value);
    }
    if (v < 0n) {
        throw new MoneyError(`amount must be non-negative, got ${v}; use the ledger direction to express outflow`);
    }
    if (v > MAX_SANTIM) {
        throw new MoneyError(`amount ${v} overflows BIGINT`);
    }
    return v;
}
export const ZERO = santim(0n);
/**
 * Construct a balance. Accepts negatives — only the accounts flagged
 * `allows_negative` can actually reach one, and the database enforces that.
 */
export function balance(value) {
    let v;
    if (typeof value === 'bigint') {
        v = value;
    }
    else if (typeof value === 'number') {
        if (!Number.isSafeInteger(value)) {
            throw new MoneyError(`balance ${value} is not a safe integer; pass a bigint or string`);
        }
        v = BigInt(value);
    }
    else {
        if (!/^-?\d+$/.test(value)) {
            throw new MoneyError(`balance string must be a plain integer, got ${JSON.stringify(value)}`);
        }
        v = BigInt(value);
    }
    if (v > MAX_SANTIM || v < MIN_SANTIM) {
        throw new MoneyError(`balance ${v} is outside the BIGINT range`);
    }
    return v;
}
/**
 * Narrow a balance to a movable amount. Throws if it is negative — you cannot
 * pay out from an account that is in the red.
 */
export function toAmount(value) {
    return santim(value);
}
/** Sum, checked against BIGINT overflow. */
export function add(a, b) {
    return santim(a + b);
}
/** Difference. Throws if it would go negative — that is always a bug here. */
export function sub(a, b) {
    if (b > a) {
        throw new MoneyError(`subtracting ${b} from ${a} would go negative`);
    }
    return santim(a - b);
}
export function sum(amounts) {
    let total = 0n;
    for (const a of amounts)
        total += a;
    return santim(total);
}
/**
 * Apply a rate in basis points (1 bps = 0.01%), rounding *down* (floor).
 *
 * Used for commission (§2): the house only ever takes a whole santim it is
 * strictly owed, never a fraction rounded in its own favour — any leftover
 * fraction goes to the player instead, via `remainderAfterBps`. That is
 * defined as a subtraction from `amount`, not a second rounding, so
 * `applyBps(x) + remainderAfterBps(x) === x` holds exactly regardless of
 * which way this rounds — the settlement posting always balances with no
 * suspense entry.
 */
export function applyBps(amount, bps) {
    if (!Number.isInteger(bps) || bps < 0 || bps > 10_000) {
        throw new MoneyError(`bps must be an integer in [0, 10000], got ${bps}`);
    }
    return santim((amount * BigInt(bps)) / 10000n);
}
/** The part of `amount` left after {@link applyBps} takes its cut. */
export function remainderAfterBps(amount, bps) {
    return sub(amount, applyBps(amount, bps));
}
/** Serialize for SQL/JSON. BIGINT columns and JSON both want the string form. */
export function toDbString(amount) {
    return amount.toString();
}
/** Insert thousands separators, e.g. "50000" -> "50,000". */
function groupThousands(birr) {
    return birr.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}
/** Human display, e.g. 12345n -> "123.45", 5000000n -> "50,000.00". Presentation only — never arithmetic. */
export function formatEtb(amount) {
    const birr = amount / SANTIM_PER_ETB;
    const cents = amount % SANTIM_PER_ETB;
    return `${groupThousands(birr.toString())}.${cents.toString().padStart(2, '0')}`;
}
/**
 * Human display floored to whole birr, e.g. 174455n -> "1744". For prize
 * figures only, where a fractional birr reads as false precision — the
 * player never actually receives a fraction of a santim less than shown, this
 * just drops the cents from the display.
 */
export function formatEtbWhole(amount) {
    return groupThousands((amount / SANTIM_PER_ETB).toString());
}
/** Parse operator/admin input like "123.45" (commas allowed) ETB into santim. */
export function parseEtb(input) {
    const m = /^(\d+)(?:\.(\d{1,2}))?$/.exec(input.trim().replace(/,/g, ''));
    if (!m) {
        throw new MoneyError(`expected an ETB amount like "123.45", got ${JSON.stringify(input)}`);
    }
    const birr = BigInt(m[1]);
    const cents = BigInt((m[2] ?? '0').padEnd(2, '0'));
    return santim(birr * SANTIM_PER_ETB + cents);
}
//# sourceMappingURL=money.js.map